<script setup>
import { formattedDate } from '@/helper';
import { computed } from 'vue';

const props = defineProps({
  history: {
    type: Object
  },
  index: {
    type: Number,
    default: 0
  }
})

const itemInfo = computed(() => {
  const type = props.history.attributes.from_action_type;
  let title = '';
  let icon = 'circle';
  let color = 'primary';
  let description = 'Points received';

  switch (type) {
    case "AssessmentProgress":
      title = 'Assessment Completed';
      icon = 'clipboard';
      color = 'info';
      description = 'Points earned from completing an assessment';
      break;
    case "InterventionProgress":
      title = 'Activity Completed';
      icon = 'activity';
      color = 'success';
      description = 'Points earned from completing an intervention activity';
      break;
    case "Option":
      title = 'Bonus Points';
      icon = 'gift';
      color = 'warning';
      description = props.history.attributes.description || 'Bonus points received';
      break;
    default:
      title = type || 'Transaction';
      icon = 'check-circle';
      color = 'primary';
      description = props.history.attributes.description || 'Points transaction';
      break;
  }

  return { title, icon, color, description };
})

const isPositive = computed(() => {
  return props.history.attributes.point_value > 0;
})
</script>

<template>
  <div class="reward-item" :style="{ animationDelay: `${index * 0.1}s` }">
    <div class="d-flex align-items-center p-3 mb-3 bg-white rounded shadow-sm border-start border-4" 
      :class="`border-${itemInfo.color}`"
    >
      <!-- Icon -->
      <div class="flex-shrink-0 me-3">
        <div class="icon-circle" :class="`bg-light-${itemInfo.color} text-${itemInfo.color}`">
          <vue-feather :type="itemInfo.icon" size="1.2rem"></vue-feather>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-grow-1">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h6 class="mb-1 fw-bold text-dark">{{ itemInfo.title }}</h6>
            <p class="mb-0 text-muted small">{{ itemInfo.description }}</p>
          </div>
          
          <!-- Points -->
          <div class="text-end ms-3">
            <h5 class="mb-0 fw-bold" :class="isPositive ? 'text-success' : 'text-danger'">
              {{ isPositive ? '+' : '' }}{{ history.attributes.point_value }}
            </h5>
            <small class="text-muted" style="font-size: 0.75rem;">
              {{ formattedDate(history.attributes.created_at, {short: true}) }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.reward-item {
  animation: slideIn 0.5s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.reward-item:hover .icon-circle {
  transform: scale(1.1);
}

.shadow-sm {
  box-shadow: 0 .125rem .25rem rgba(0,0,0,.075)!important;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.reward-item:hover .shadow-sm {
  box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
  transform: translateY(-2px);
}
</style>